import { Disclosure } from "@headlessui/react";
import classNames from "classnames";
import type { CurrentRefinementsConnectorParamsItem } from "instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import {
  ClearRefinements,
  RangeInput,
  RefinementList,
  RefinementListProps,
  useClearRefinements,
  useCurrentRefinements,
  useInfiniteHits,
  useSortBy,
} from "react-instantsearch";

import { useWindowSize } from "~/contexts/breakpoint";
import { useConfiguration } from "~/contexts/configuration";
import { useServerSideProps } from "~/contexts/server-side-props";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import { assertEditorialType } from "~/utils/editorial-utils";

import Dialog from "../common/dialog";
import Grid from "../common/grid";
import Icon from "../common/icon";
import Pane from "../common/pane";
import checkboxStyles from "../form/checkbox.module.scss";
import AlgoliaSortDialog from "../search/algolia-sort-dialog";
import styles from "./search-filter-dialog.module.scss";
import { RatingMenu } from "./search-filter-reviews";

const transformItems: RefinementListProps["transformItems"] = (items) => {
  return items
    .filter((item) => item.label != "N/A")
    .map((item) => ({
      ...item,
      label: item.label.toLocaleLowerCase() + ` (${item.count})`,
    }));
};

type Props = { disableFilters: boolean };

export default function SearchFilterDialog({ disableFilters }: Props) {
  const t = useTranslations();
  const { width } = useWindowSize();
  const { algoliaIndexId } = useConfiguration();
  const [filtersDialog, setFiltersDialog] = useState(false);
  const [sortingDialog, setSortingDialog] = useState(false);

  const { refine } = useClearRefinements();

  const { items } = useCurrentRefinements();
  const { results } = useInfiniteHits();

  const { serverSideProps } = useServerSideProps();
  const contentUtils = serverSideProps.contentUtils;
  assertEditorialType<Editorials.ContentUtils>(contentUtils, "contentUtils");
  const algoliaSorting = contentUtils.fields.algoliaSorting?.map((data) => {
    return {
      ...data,
      items: data.items.map((item) => {
        return { ...item, value: algoliaIndexId + item.value };
      }),
    };
  });

  const { currentRefinement } = useSortBy({
    items: algoliaSorting?.flatMap((data) => data.items) ?? [{ label: "", value: algoliaIndexId }],
  });

  const totalRefinementsApplied = useMemo(
    () => items.flatMap((el) => el.refinements).filter((el) => !el.attribute.includes("categories")).length,
    [items]
  );

  function calculateAppliedFilterCount(refinements: CurrentRefinementsConnectorParamsItem[], attributeId: string) {
    const listWithCounterSetToOne = ["averageRating", "prices.value", "prices.formattedDiscountPerc"];
    const foundRefinements = refinements.find((refinement) => refinement.attribute == attributeId);
    let returnString = "";

    if (foundRefinements) {
      if (listWithCounterSetToOne.includes(attributeId)) {
        returnString = `(1)`;
      } else if (foundRefinements.refinements.length) {
        returnString = ` (${foundRefinements.refinements.length})`;
      }
    }

    return returnString;
  }

  //filter
  const multicolorFilter = results?.disjunctiveFacets.find((facet) => facet.name == "multicolor");
  const hasMulticolor =
    multicolorFilter && Object.keys(multicolorFilter.data).filter((data) => data != "N/A").length > 0;

  const finishEffectFilter = results?.disjunctiveFacets.find((facet) => facet.name == "finishEffect");
  const hasFinishEffect =
    finishEffectFilter && Object.keys(finishEffectFilter.data).filter((data) => data != "N/A").length > 0;

  const coverageFilter = results?.disjunctiveFacets.find((facet) => facet.name == "coverage");
  const hasCoverage = coverageFilter && Object.keys(coverageFilter.data).filter((data) => data != "N/A").length > 0;

  const hasSpf = results?.disjunctiveFacets.find((facet) => facet.name == "spf");

  const hasWaterproof = results?.disjunctiveFacets.some((facet) => facet.name == "waterproof");

  const hasAverageRating = results?.disjunctiveFacets.some((facet) => facet.name == "averageRating");

  const hasPrices = results?.disjunctiveFacets.some((facet) => facet.name == "prices.value");

  const hasDiscounts = results?.disjunctiveFacets.some((facet) => facet.name == "prices.formattedDiscountPerc");

  const hasFilters =
    !disableFilters &&
    (hasMulticolor ||
      hasFinishEffect ||
      hasCoverage ||
      hasSpf ||
      hasWaterproof ||
      hasAverageRating ||
      hasPrices ||
      hasDiscounts);

  return (
    <>
      <div className={styles.wrapperFiltersAndSorting}>
        {hasFilters && results?.disjunctiveFacets && results.disjunctiveFacets.length > 0 && (
          <button type="button" className={styles.cta} onClick={() => setFiltersDialog(true)}>
            <Icon name="filters" width={16} height={16} />
            {t("generic.filters")}
            {totalRefinementsApplied > 0 && <span className={styles.filtersCount}>{totalRefinementsApplied}</span>}
          </button>
        )}
        {algoliaSorting && (
          <button type="button" className={styles.cta} onClick={() => setSortingDialog(true)}>
            <Icon name="sort" width={16} height={16} />
            {t("generic.sort_by")}
            {algoliaIndexId !== currentRefinement && <span className={styles.filtersCount}>1</span>}
          </button>
        )}
      </div>
      {/* filters */}
      <Dialog
        isOpen={filtersDialog}
        onClose={() => setFiltersDialog(false)}
        title={
          <>
            <Icon name="filters" />
            <span>{t("generic.filters")}</span>
          </>
        }
        customStyles={styles}
      >
        {width != undefined && width >= 640 ? (
          <Grid className={styles.filtersGrid}>
            {/* color filter pane */}
            {hasMulticolor && (
              <Pane className={styles.filterPane}>
                <div className={styles.filterPaneHeading}>
                  <p className={styles.title}>{t(`algolia.filter.colors`)}</p>
                  <ClearRefinements
                    includedAttributes={["multicolor"]}
                    classNames={{ button: styles.buttonClearRefinements }}
                    translations={{
                      resetButtonText: t("generic.clear") + calculateAppliedFilterCount(items, "multicolor"),
                    }}
                  />
                </div>
                <RefinementList
                  attribute="multicolor"
                  transformItems={transformItems}
                  limit={Constants.MAX_NUMBER_VALUES_OF_FACET}
                  classNames={{
                    list: styles.filterListMultiColor,
                    checkbox: styles.checkboxInputMultiColor,
                    searchBox: styles.searchBox,
                    count: "d-none",
                    labelText: "sr-only",
                  }}
                />
              </Pane>
            )}
            {/* finishEffect filter pane */}
            {hasFinishEffect && (
              <Pane className={styles.filterPane}>
                <div className={styles.filterPaneHeading}>
                  <p className={styles.title}>{t(`algolia.filter.finish`)}</p>
                  <ClearRefinements
                    includedAttributes={["finishEffect"]}
                    classNames={{ button: styles.buttonClearRefinements }}
                    translations={{
                      resetButtonText: t("generic.clear") + calculateAppliedFilterCount(items, "finishEffect"),
                    }}
                  />
                </div>
                <RefinementList
                  attribute="finishEffect"
                  transformItems={transformItems}
                  classNames={{
                    list: styles.filterList,
                    label: classNames(styles.filterCheckbox, checkboxStyles.container),
                    checkbox: styles.checkboxInput,
                    count: "d-none",
                  }}
                  limit={Constants.MAX_NUMBER_VALUES_OF_FACET}
                />
              </Pane>
            )}
            {/* coverage filter pane */}
            {hasCoverage && (
              <Pane className={styles.filterPane}>
                <div className={styles.filterPaneHeading}>
                  <p className={styles.title}>{t(`algolia.filter.coverage`)}</p>
                  <ClearRefinements
                    includedAttributes={["coverage"]}
                    classNames={{ button: styles.buttonClearRefinements }}
                    translations={{
                      resetButtonText: t("generic.clear") + calculateAppliedFilterCount(items, "coverage"),
                    }}
                  />
                </div>
                <RefinementList
                  attribute="coverage"
                  transformItems={transformItems}
                  classNames={{
                    list: styles.filterList,
                    label: classNames(styles.filterCheckbox, checkboxStyles.container),
                    checkbox: styles.checkboxInput,
                    count: "d-none",
                  }}
                  limit={Constants.MAX_NUMBER_VALUES_OF_FACET}
                />
              </Pane>
            )}
            {/* ratings filter pane */}
            {hasAverageRating && (
              <Pane className={styles.filterPane}>
                <div className={styles.filterPaneHeading}>
                  <p className={styles.title}>{t(`algolia.filter.averageRating`)}</p>
                  <ClearRefinements
                    includedAttributes={["averageRating"]}
                    classNames={{ button: styles.buttonClearRefinements }}
                    translations={{
                      resetButtonText: t("generic.clear") + calculateAppliedFilterCount(items, "averageRating"),
                    }}
                  />
                </div>
                <RatingMenu
                  attribute="averageRating"
                  classNames={{
                    list: styles.filterList,
                    item: styles.filterItem,
                  }}
                />
              </Pane>
            )}
            {/* price filter pane */}
            {hasPrices && (
              <Pane className={styles.filterPane}>
                <div className={styles.filterPaneHeading}>
                  <p className={styles.title}>{t(`algolia.filter.price`)}</p>
                  <ClearRefinements
                    includedAttributes={["prices.value"]}
                    classNames={{ button: styles.buttonClearRefinements }}
                    translations={{
                      resetButtonText: t("generic.clear") + calculateAppliedFilterCount(items, "prices.value"),
                    }}
                  />
                </div>
                <div className={styles.filterList}>
                  <RangeInput
                    attribute="prices.value"
                    classNames={{
                      form: styles.rangeInputForm,
                      input: styles.rangeInput,
                      submit: styles.submitButton,
                    }}
                  />
                </div>
              </Pane>
            )}
            {/* discounted price filter pane */}
            {hasDiscounts && (
              <Pane className={styles.filterPane}>
                <div className={styles.filterPaneHeading}>
                  <p className={styles.title}>{t(`algolia.filter.discountedPrice`)}</p>
                  <ClearRefinements
                    includedAttributes={["prices.formattedDiscountPerc"]}
                    classNames={{ button: styles.buttonClearRefinements }}
                    translations={{
                      resetButtonText:
                        t("generic.clear") + calculateAppliedFilterCount(items, "prices.formattedDiscountPerc"),
                    }}
                  />
                </div>
                <div className={styles.filterList}>
                  <RangeInput
                    attribute="prices.formattedDiscountPerc"
                    classNames={{
                      form: styles.rangeInputForm,
                      input: styles.rangeInput,
                      submit: styles.submitButton,
                    }}
                  />
                </div>
              </Pane>
            )}
            {/* waterproof filter pane */}
            {hasWaterproof && (
              <Pane className={styles.filterPane}>
                <div className={styles.filterPaneHeading}>
                  <p className={styles.title}>{t(`algolia.filter.waterproof`)}</p>
                  <ClearRefinements
                    includedAttributes={["waterproof"]}
                    classNames={{ button: styles.buttonClearRefinements }}
                    translations={{
                      resetButtonText: t("generic.clear") + calculateAppliedFilterCount(items, "waterproof"),
                    }}
                  />
                </div>
                <RefinementList
                  attribute="waterproof"
                  transformItems={transformItems}
                  classNames={{
                    list: styles.filterList,
                    label: classNames(styles.filterCheckbox, checkboxStyles.container),
                    checkbox: styles.checkboxInput,
                    searchBox: styles.searchBox,
                    count: "d-none",
                  }}
                />
              </Pane>
            )}
            {/* spf options filter pane */}
            {hasSpf && (
              <Pane className={styles.filterPane}>
                <div className={styles.filterPaneHeading}>
                  <p className={styles.title}>{t(`algolia.filter.spf`)}</p>
                  <ClearRefinements
                    includedAttributes={["spf"]}
                    classNames={{ button: styles.buttonClearRefinements }}
                    translations={{
                      resetButtonText: t("generic.clear") + calculateAppliedFilterCount(items, "spf"),
                    }}
                  />
                </div>
                <RefinementList
                  attribute="spf"
                  transformItems={transformItems}
                  classNames={{
                    list: styles.filterList,
                    label: classNames(styles.filterCheckbox, checkboxStyles.container),
                    checkbox: styles.checkboxInput,
                    searchBox: styles.searchBox,
                    count: "d-none",
                  }}
                />
              </Pane>
            )}
          </Grid>
        ) : (
          <Pane className={styles.filterPane}>
            {/* color accordion */}
            {hasMulticolor && (
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className={styles.accordionButton}>
                      <p className={styles.title}>
                        {t(`algolia.filter.colors`) + calculateAppliedFilterCount(items, "multicolor")}
                      </p>
                      <Icon name={open ? "chevron-up" : "chevron-down"} aria-hidden width={16} height={16} />
                    </Disclosure.Button>
                    <Disclosure.Panel static className={styles.accordionPanel}>
                      <div className={styles.panelInner}>
                        <RefinementList
                          attribute="multicolor"
                          transformItems={transformItems}
                          classNames={{
                            list: styles.filterListMultiColor,
                            checkbox: styles.checkboxInputMultiColor,
                            searchBox: styles.searchBox,
                            count: "d-none",
                            labelText: "sr-only",
                          }}
                          limit={Constants.MAX_NUMBER_VALUES_OF_FACET}
                        />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            )}
            {/* finishEffect accordion */}
            {hasFinishEffect && (
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className={styles.accordionButton}>
                      <p className={styles.title}>
                        {t(`algolia.filter.finish`) + calculateAppliedFilterCount(items, "finishEffect")}
                      </p>
                      <Icon name={open ? "chevron-up" : "chevron-down"} aria-hidden width={16} height={16} />
                    </Disclosure.Button>
                    <Disclosure.Panel static className={styles.accordionPanel}>
                      <div className={styles.panelInner}>
                        <RefinementList
                          attribute="finishEffect"
                          transformItems={transformItems}
                          classNames={{
                            list: styles.filterList,
                            label: classNames(styles.filterCheckbox, checkboxStyles.container),
                            checkbox: styles.checkboxInput,
                            count: "d-none",
                          }}
                          limit={Constants.MAX_NUMBER_VALUES_OF_FACET}
                        />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            )}
            {/* coverage accordion */}
            {hasCoverage && (
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className={styles.accordionButton}>
                      <p className={styles.title}>
                        {t(`algolia.filter.coverage`) + calculateAppliedFilterCount(items, "coverage")}
                      </p>
                      <Icon name={open ? "chevron-up" : "chevron-down"} aria-hidden width={16} height={16} />
                    </Disclosure.Button>
                    <Disclosure.Panel static className={styles.accordionPanel}>
                      <div className={styles.panelInner}>
                        <RefinementList
                          attribute="coverage"
                          transformItems={transformItems}
                          classNames={{
                            list: styles.filterList,
                            label: classNames(styles.filterCheckbox, checkboxStyles.container),
                            checkbox: styles.checkboxInput,
                            count: "d-none",
                          }}
                          limit={Constants.MAX_NUMBER_VALUES_OF_FACET}
                        />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            )}
            {/* averageRating accordion */}
            {hasAverageRating && (
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className={styles.accordionButton}>
                      <p className={styles.title}>
                        {t(`algolia.filter.averageRating`) + calculateAppliedFilterCount(items, "averageRating")}
                      </p>
                      <Icon name={open ? "chevron-up" : "chevron-down"} aria-hidden width={16} height={16} />
                    </Disclosure.Button>
                    <Disclosure.Panel static className={styles.accordionPanel}>
                      <div className={styles.panelInner}>
                        <RatingMenu
                          attribute="averageRating"
                          classNames={{
                            list: styles.filterList,
                            item: styles.filterItem,
                          }}
                        />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            )}
            {/* price accordion */}
            {hasPrices && (
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className={styles.accordionButton}>
                      <p className={styles.title}>
                        {t(`algolia.filter.price`) + calculateAppliedFilterCount(items, "prices.value")}
                      </p>
                      <Icon name={open ? "chevron-up" : "chevron-down"} aria-hidden width={16} height={16} />
                    </Disclosure.Button>
                    <Disclosure.Panel static className={styles.accordionPanel}>
                      <div className={styles.panelInner}>
                        <RangeInput
                          attribute="prices.value"
                          classNames={{
                            form: styles.rangeInputForm,
                            input: styles.rangeInput,
                            submit: styles.submitButton,
                          }}
                        />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            )}
            {/* discounted price accordion*/}
            {hasDiscounts && (
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className={styles.accordionButton}>
                      <p className={styles.title}>
                        {t(`algolia.filter.discountedPrice`) +
                          calculateAppliedFilterCount(items, "prices.formattedDiscountPerc")}
                      </p>
                      <Icon name={open ? "chevron-up" : "chevron-down"} aria-hidden width={16} height={16} />
                    </Disclosure.Button>
                    <Disclosure.Panel static className={styles.accordionPanel}>
                      <div className={styles.panelInner}>
                        <RangeInput
                          attribute="prices.formattedDiscountPerc"
                          classNames={{
                            form: styles.rangeInputForm,
                            input: styles.rangeInput,
                            submit: styles.submitButton,
                          }}
                        />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            )}
            {/* waterproof accordion */}
            {hasWaterproof && (
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className={styles.accordionButton}>
                      <p className={styles.title}>
                        {t(`algolia.filter.waterproof`) + calculateAppliedFilterCount(items, "waterproof")}
                      </p>
                      <Icon name={open ? "chevron-up" : "chevron-down"} aria-hidden width={16} height={16} />
                    </Disclosure.Button>
                    <Disclosure.Panel static className={styles.accordionPanel}>
                      <div className={styles.panelInner}>
                        <RefinementList
                          attribute="waterproof"
                          transformItems={transformItems}
                          classNames={{
                            list: styles.filterList,
                            label: classNames(styles.filterCheckbox, checkboxStyles.container),
                            checkbox: styles.checkboxInput,
                            count: "d-none",
                          }}
                        />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            )}
            {/* spf options accordion */}
            {hasSpf && (
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className={styles.accordionButton}>
                      <p className={styles.title}>
                        {t(`algolia.filter.spf`) + calculateAppliedFilterCount(items, "spf")}
                      </p>
                      <Icon name={open ? "chevron-up" : "chevron-down"} aria-hidden width={16} height={16} />
                    </Disclosure.Button>
                    <Disclosure.Panel static className={styles.accordionPanel}>
                      <div className={styles.panelInner}>
                        <RefinementList
                          attribute="spf"
                          transformItems={transformItems}
                          classNames={{
                            list: styles.filterList,
                            label: classNames(styles.filterCheckbox, checkboxStyles.container),
                            checkbox: styles.checkboxInput,
                            count: "d-none",
                          }}
                        />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            )}
          </Pane>
        )}
        <Grid className={styles.gridButtons}>
          <div className={styles.wrapperButtons}>
            <button type="button" className={styles.button} onClick={() => refine()}>
              {t("generic.clear_all")}
              {totalRefinementsApplied > 0 && <span className={styles.highlightValue}>{totalRefinementsApplied}</span>}
            </button>
            <button type="button" className={styles.button} onClick={() => setFiltersDialog(false)}>
              {t("generic.view_results")}
              <span className={styles.highlightValue}>{results?.nbHits}</span>
            </button>
          </div>
        </Grid>
      </Dialog>
      {algoliaSorting && (
        <AlgoliaSortDialog
          algoliaSorting={algoliaSorting}
          sortingDialog={sortingDialog}
          setSortingDialog={setSortingDialog}
        />
      )}
    </>
  );
}
